(ns common.schema.entities.counterparty-diligence.templates.util
  (:require
   [clojure.string :as string]
   [common.country :as country]
   [common.util :as cu]))

(defn nationality-rows
  "Rows of a :person/nationalities answer:
   [{:nationality <country-kw> :id-number <string>} ...]

   When the answer key is absent from `data`, falls back to a single row
   built from the legacy :person/nationality +
   :person/national-identification-number answers so processes saved before
   the multi-nationality field keep their data. An empty vector present
   under the key means the respondent removed every row — the legacy
   fallback does not apply then."
  [data]
  (if (contains? data :person/nationalities)
    (:person/nationalities data)
    (let [nationality (:person/nationality data)
          id-number (not-empty (str (:person/national-identification-number data)))]
      (when (or nationality id-number)
        [{:nationality nationality
          :id-number   id-number}]))))

(defn nationality-countries
  "Country keywords of a person's nationalities (legacy fallback included),
   first entry first. Used for screening, which supports several countries
   per person."
  [data]
  (->> (nationality-rows data)
       (keep :nationality)
       vec))

(defn nationality-rows->display
  "Human-readable one-line rendering of nationality rows, e.g.
   \"Finland - 123456; Sweden - 654321\"."
  [rows lang-kw]
  (->> rows
       (map (fn [{:keys [nationality id-number]}]
              (->> [(country/get-label nationality lang-kw)
                    id-number]
                   (remove cu/blank-value?)
                   (string/join " - "))))
       (remove cu/blank-value?)
       (string/join "; ")))

(defn field-visible?
  [{:keys [field field-config data e-signature-required?]}]
  (let [visibility-fn (or (:visibility-fn field-config)
                          (constantly true))
        visible? (visibility-fn {:field field
                                 :data data
                                 :e-signature-required? e-signature-required?})]
    visible?))

(defn resolve-visible-fields
  [{:keys [template data e-signature-required?]}]
  (->> template
       :template/schema
       (filterv (fn [[field field-config]]
                  (field-visible? {:field field
                                   :data data
                                   :field-config field-config
                                   :e-signature-required? e-signature-required?})))
       (mapv first)
       set))

(defn extract-file-ids
  [template data]
  (letfn [(extract [template data]
            (->> data
                 (reduce-kv
                  (fn [acc field value]
                    (let [field-schema (get-in template [:template/schema field])
                          data-type (:data-type field-schema)
                          item-template (:item-template field-schema)]
                      (cond
                        (not value)
                        acc

                        (= data-type :data.type/file)
                        (if (coll? value)
                          (into acc value)
                          (conj acc value))

                        (and (= data-type :data.type/vector)
                             item-template)
                        (->> value
                             (mapcat (partial extract item-template))
                             (into acc))

                        :else acc)))
                  [])))]
    (->> data
         (extract template)
         (remove nil?)
         distinct
         vec)))

(defn update-file-ids
  "Recursively updates file IDs in the data structure based on the provided template.
  
  `data` is the original data map.
  `template` is the template schema map with a top-level :template/schema key.
  `new-id-map` is a map of old file IDs to new file IDs.
  
  This function returns a new data map with file IDs replaced."
  [{:keys [data template new-id-map]}]
  (letfn [(update-fields [template data]
            (->> data
                 (reduce-kv
                  (fn [acc field value]
                    (let [field-schema (get-in template [:template/schema field])
                          data-type (:data-type field-schema)
                          item-template (:item-template field-schema)]
                      (cond
                        (= data-type :data.type/file)
                        (if (coll? value)
                          (->> value
                               (mapv #(get new-id-map %))
                               (assoc acc field))
                          (->> value
                               (get new-id-map)
                               (assoc acc field)))

                        (and (= data-type :data.type/vector)
                             item-template)
                        (->> value
                             (mapv (partial update-fields item-template))
                             (assoc acc field))

                        :else (assoc acc field value))))
                  {})))]
    (update-fields template data)))
